/**
 * Phase 9 (Phase 20: per campaign group) — the Auto-send tile of the plan's
 * facts strip: a switch for every campaign group at once (user, 19/10/2026:
 * "switch all on / off" — on asks first, real SMS; off stops every group),
 * the country's state in one word ("Off", "On", "Halted"), the groups and
 * today's sends under it, and the status — the next send, or who sends. A
 * click anywhere else opens the table of groups, each with its own switch
 * and settings.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Send } from 'lucide-react';

import { Switch } from '@/components/ui';
import { cn } from '@/lib/utils';

import type { DropGenPlanDetail } from '../../../drop-generator.types';
import { useSchedulerStatus } from '../../../useDropGenerator';
import { FactTile } from '../schedule/FactTile';
import { AllGroupsConfirmDialog } from './all/AllGroupsConfirmDialog';
import { useAllGroupsSwitch } from './all/use-all-groups-switch';
import { countryTile } from './autosend-tile';
import { groupRows } from './groups/group-rows';

const HINT =
  'Auto-send is set per campaign group, on its master config: on, the engine sends each draft of the group at its planned time, holds one at the group’s daily cap, and halts the group on its stop-loss. Off: you send. The switch here turns every group on or off at once; the table sets each one. Kill stops every group.';

export const AutoSendSegment: FC<{ plan: DropGenPlanDetail; open: boolean; onToggle: () => void }> = ({ plan, open, onToggle }) => {
  const status = useSchedulerStatus();
  const mine = status.data?.plans.find((p) => p.countryKey === plan.countryKey);
  const groups = groupRows(plan, mine);
  const server = status.data?.sendModes;
  const sw = useAllGroupsSwitch(plan.countryKey, groups);
  const tile = countryTile(plan, mine, groups, server);
  const action = { label: open ? 'Hide the campaign groups' : 'Show the campaign groups — each one’s switch and settings', onClick: onToggle, expanded: open };
  const alarm = tile.tone === 'halted' || tile.tone === 'stuck';
  const anyOn = groups.some((g) => g.config.autoSend);
  return (
    <FactTile icon={Send} label="Auto-send" hint={HINT} action={action} detail={tile.detail} status={tile.status} testId="fact-autosend">
      <span className="pointer-events-auto relative inline-flex">
        <Switch
          checked={anyOn}
          disabled={!sw.canSwitch || (!anyOn && sw.onMode === null)}
          onCheckedChange={(on) => (on ? sw.onMode && sw.pick(sw.onMode) : sw.pick('off'))}
          aria-label="Auto-send in every campaign group"
        />
      </span>
      <span className={cn('truncate', alarm && 'text-warning')} data-testid="autosend-fact">
        {tile.value}
      </span>
      <AllGroupsConfirmDialog open={sw.confirming} groups={groups} mode={sw.confirmMode} autoGenerateOff={!plan.autoGenerate} busy={sw.busy} onConfirm={sw.confirm} onCancel={sw.cancel} />
    </FactTile>
  );
};
